<template>
  <div
    v-if="getQR.length > 0"
    ref="knw_widget_qrlist_wrapper"
    class="knw_widget_qrlist_wrapper"
    :style="{ height: getWrapperHeight, 'max-height': '220px' }"
  >
    <div
      ref="knw_widget_qrlist"
      class="knw_widget_qrlist"
      role="group"
      :aria-label="t('input.qr.listLabel')"
    >
      <button
        v-for="(qr, i) in getQR"
        :key="`${i}${qr}`"
        class="knw_widget_qr"
        @click="handleQuickReply(qr)"
      >
        {{ qr.text }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, onUpdated, ref } from "vue";
import signals from "@/common/signals";
import type { Intent } from "@/types";
import { useI18n } from "@/composables/useI18n.ts";
import { usePaddingHelper } from "@/composables/usePaddingHelper.ts";
export interface QuickReplyProps {
  quickReplies?: Array<Intent>;
}

const { setHeight } = usePaddingHelper();

const props = withDefaults(defineProps<QuickReplyProps>(), { quickReplies: () => [] });

const emit = defineEmits(["on-intent"]);
const { t } = useI18n();
const knw_widget_qrlist = ref();

const dynamicHeight = ref(0);
const clicked = ref(false);

const getQR = computed(() => props.quickReplies);
const getWrapperHeight = computed(() => `${dynamicHeight.value}px`);

onMounted(() => {
  // read height and set a local state var and bind it to the style Element (line 2 and 28)
  dynamicHeight.value = knw_widget_qrlist.value.clientHeight;
});

onUpdated(() => {
  dynamicHeight.value = knw_widget_qrlist.value.clientHeight;
  setHeight(dynamicHeight.value);
});

onBeforeUnmount(() => {
  // reset height to 0 on unmount
  dynamicHeight.value = 0;
  setHeight(0);
});

const handleQuickReply = (intent: Intent) => {
  if (clicked.value) return;
  clicked.value = true;
  // reset click guard because we resuse the component
  // and we need to reset the state for next qr
  setTimeout(() => {
    clicked.value = false;
  }, 500);
  signals.click("quickreplies", { source: "quickreplies", text: intent.text });
  emit("on-intent", intent);
};
</script>

<style lang="scss">
.knw_widget_qrlist_wrapper {
  overflow-y: auto;
  scrollbar-gutter: stable;

  margin-right: 2px;

  background: var(--moin-quickreplybar-background, transparent);

  &::-webkit-scrollbar {
    width: 4px;
    /* Adjust the width to make it thin */
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    /* Customize the scrollbar thumb color */
    border-radius: 2px;
    /* Add rounded corners */
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    /* Customize the scrollbar track color */
  }

  .knw_widget_qrlist {
    // overflow: auto;
    background: var(--moin-quickreplybar-background, transparent);
    z-index: 90;
    display: flex;
    gap: 4px;
    flex-flow: row wrap;
    justify-content: flex-end;
    align-items: center;
    padding: 4px 4px;
    padding-bottom: 10px;
    height: auto;
    min-height: 48px;
    position: relative;
  }
}

.knw_widget_qr {
  // I think this was a leaking customer style defense
  // and should not be longer needed
  text-transform: none !important;
  word-break: normal;
  overflow-wrap: anywhere;

  border: 1px solid var(--moin-widget-button-bordercolor);
  border-radius: 20px;
  height: auto;
  min-height: 32px;
  padding: 4px 8px;
  transition: border-color 0.2s ease-in-out;
  // font: $kw-default-font-small;
  @include default-font-small;
  //  transition: background-color $kw-default-transition-speed;
  @include default-focus;

  background: var(--moin-quickreplybar-pill-background, $kw-global-background-white);
  color: var(--moin-quickreplybar-pill-textcolor, $kw-global-textcolor);
  text-align: left;

  &:hover,
  &:focus {
    cursor: pointer;
    box-shadow: none;
    border-color: $kw-default-button-border-color-hover;
    transition: border-color 0.2s ease-in-out;
    color: var(--moin-quickreplybar-pill-textcolor-hover, $kw-global-textcolor);
    background: var(--moin-quickreplybar-pill-background-hover, $kw-global-background-white);
  }

  @media (min-width: $kw-widget-breakpoint) {
    min-height: 32px;
    @include default-font-small;

    padding: 4px 12px;
  }
}
</style>
